<template>
  <div>
    <AccordionBase
      text="Текст"
      :showChevronWithActions="true"
      :classContent="[style.InputStyles]"
      :class="[style.block, style.contentBlock]"
    >
      <template #actions>
        <div />
      </template>

      <InputStyle
        description="Заголовок"
        placeholder="Введите заголовок"
        size="M"
        textarea
        :modelValue="questionTitle"
        @update:modelValue="setQuestionTitle"
      />

      <InputStyle
        description="Описание"
        placeholder="Введите описание"
        size="M"
        textarea
        :modelValue="questionDescription"
        @update:modelValue="setQuestionDescription"
      />

      <InputStyle
        description="Текст под кнопкой"
        placeholder="Введите текст"
        size="M"
        :modelValue="textBellowButton"
        @update:modelValue="setTextBellowButton"
      />

      <InputStyle
        description="Подсказка"
        placeholder="Введите подсказку"
        size="M"
        :modelValue="questionPlaceholder"
        @update:modelValue="setQuestionPlaceholder"
      />
    </AccordionBase>

    <AccordionBase
      text="Варианты ответа"
      :showChevronWithActions="true"
      :classContent="[style.fields]"
      :classHeader="[style.variantsHeader]"
      :class="style.block"
    >
      <template #actions>
        <ButtonIcon
          variant="ghost"
          size="28"
          :icon="addIcon"
          :class="style.addButton"
          @click.stop="addVariant"
        />
      </template>

      <InputStyle
        v-for="(variant, variantIndex) in normalizedVariants"
        :key="variant._key"
        :classes="[style.input]"
        size="M"
        placeholder="Введите текст"
        :modelValue="variant.text || ''"
        @update:modelValue="setVariantText(variantIndex, $event)"
      >
        <template #actions>
          <ActionMenu
            v-model="variantActionMenuOpen[variantIndex]"
            :actions="getVariantActions(variantIndex)"
            :teleport="true"
            :closeOnScroll="true"
            :closeOnResize="true"
            placement="bottom-end"
            :offset="8"
          >
            <template #trigger="{toggle}">
              <ButtonIcon
                :icon="moreIcon"
                :class="style.inputButton"
                @click.stop="toggle"
                variant="ghost"
                size="28"
              />
            </template>
          </ActionMenu>
        </template>
      </InputStyle>
    </AccordionBase>
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType, markRaw} from "vue";

import AccordionBase from "@/elements/UI/accordion/AccordionBase.vue";
import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";
import GhostButton from "@/elements/UI/button/ButtonStyle/Default/ghost/GhostButton.vue";
import ActionMenu from "@/elements/UI/popup/ActionMenu/ActionMenu.vue";

import style from "../../QuestionPanelComponent.module.scss";

import addIcon from "$images/icons/button/Name=Add, Size=16, Tone=Default.svg";
import moreIcon from "$images/icons/button/Name=More, Size=16, Tone=Default.svg";
import chevronUpIcon from "$images/icons/button/Name=Chevron-up, Size=16, Tone=Default.svg";
import chevronDownIcon from "$images/icons/button/Name=Chevron-down, Size=16, Tone=Default.svg";
import deleteIcon from "$images/icons/button/Name=Delete, Size=16, Tone=Error.svg";

import {questions, QuestionVariant} from "@/page/quiz/typeQuiz";
import ButtonIcon from "@/elements/UI/button/ButtonIcon/ButtonIcon.vue";
import GhostButtonError from "@/elements/UI/button/ButtonStyle/Error/ghost/GhostButtonError.vue";
import {modifyQuiz} from "@/page/quiz/scriptQuiz";

export default defineComponent({
  name: "DropdownQuestionContent",

  components: {
    ButtonIcon,
    GhostButton,
    InputStyle,
    AccordionBase,
    ActionMenu
  },

  props: {
    question: {
      type: Object as PropType<questions | null>,
      default: null
    },

    questionIndex: {
      type: Number,
      default: -1
    }
  },

  data() {
    return {
      style,
      addIcon,
      moreIcon,
      variantActionMenuOpen: [] as boolean[]
    };
  },

  mounted() {
    this.ensureVariants();
  },

  methods: {
    createVariantKey(variantIndex: number): string {
      return `select_${this.questionIndex}_${variantIndex}_${Date.now()}_${Math.random()
        .toString(16)
        .slice(2)}`;
    },

    ensureVariants(): void {
      if (!this.question) {
        return;
      }

      if (!Array.isArray(this.question.variants)) {
        this.setVariants([]);
        return;
      }

      const hasVariantsWithoutKeys = this.question.variants.some(
        (variant: QuestionVariant) => !variant._key
      );

      if (!hasVariantsWithoutKeys) {
        return;
      }

      const variants = this.question.variants.map(
        (variant: QuestionVariant, variantIndex: number) => {
          return {
            text: variant.text || "",
            img: variant.img || "",
            _key: variant._key || this.createVariantKey(variantIndex)
          };
        }
      );

      this.setVariants(variants);
    },

    setQuestionTitle(value: string): void {
      if (!this.question) {
        return;
      }

      this.question.question = value;
    },

    setQuestionDescription(value: string): void {
      if (!this.question) {
        return;
      }

      this.ensureContacts();

      this.question.contacts!.paragraph = value;
    },

    setQuestionPlaceholder(value: string): void {
      if (!this.question) {
        return;
      }

      this.question.placeholder = value;
    },

    addVariant(): void {
      const variants = [
        ...this.normalizedVariants,
        {
          text: "",
          img: "",
          _key: this.createVariantKey(this.normalizedVariants.length)
        }
      ];

      this.setVariants(variants);
    },

    setVariantText(variantIndex: number, value: string): void {
      const variants = [...this.normalizedVariants];

      if (!variants[variantIndex]) {
        return;
      }

      variants[variantIndex] = {
        ...variants[variantIndex],
        text: value
      };

      this.setVariants(variants);
    },

    moveVariantUp(variantIndex: number): void {
      if (variantIndex <= 0) {
        return;
      }

      const variants = [...this.normalizedVariants];

      [variants[variantIndex - 1], variants[variantIndex]] = [
        variants[variantIndex],
        variants[variantIndex - 1]
      ];

      this.setVariants(variants);
      this.closeAllVariantActionMenus();
    },

    moveVariantDown(variantIndex: number): void {
      if (variantIndex >= this.normalizedVariants.length - 1) {
        return;
      }

      const variants = [...this.normalizedVariants];

      [variants[variantIndex + 1], variants[variantIndex]] = [
        variants[variantIndex],
        variants[variantIndex + 1]
      ];

      this.setVariants(variants);
      this.closeAllVariantActionMenus();
    },

    deleteVariant(variantIndex: number): void {
      const variants = [...this.normalizedVariants];

      variants.splice(variantIndex, 1);

      this.setVariants(variants);
      this.closeAllVariantActionMenus();
    },

    setVariants(variants: QuestionVariant[]): void {
      if (!this.question) {
        return;
      }

      this.question.variants = variants.map((variant, variantIndex) => {
        return {
          text: variant.text || "",
          img: variant.img || "",
          _key: variant._key || this.createVariantKey(variantIndex)
        };
      });

      this.syncVariantActionMenuState();
    },

    syncVariantActionMenuState(): void {
      this.variantActionMenuOpen = this.normalizedVariants.map(
        (_variant, variantIndex) => {
          return Boolean(this.variantActionMenuOpen[variantIndex]);
        }
      );
    },

    closeVariantActionMenu(variantIndex: number): void {
      this.variantActionMenuOpen[variantIndex] = false;
    },

    closeAllVariantActionMenus(): void {
      this.variantActionMenuOpen = this.normalizedVariants.map(() => false);
    },

    getVariantActions(variantIndex: number): ListActionItem[] {
      return [
        {
          text: "Переместить вверх",
          icon: chevronUpIcon,
          disabled: variantIndex <= 0,
          variant: "button",
          handler: () => this.moveVariantUp(variantIndex)
        },
        {
          text: "Переместить вниз",
          icon: chevronDownIcon,
          disabled: variantIndex >= this.normalizedVariants.length - 1,
          variant: "button",
          handler: () => this.moveVariantDown(variantIndex)
        },
        {
          text: "Удалить",
          icon: deleteIcon,
          variant: "button",
          buttonComponent: markRaw(GhostButtonError),
          handler: () => this.deleteVariant(variantIndex)
        }
      ];
    },

    ensureContacts(): void {
      if (!this.question) {
        return;
      }

      if (!this.question.contacts) {
        this.question.contacts = {};
      }
    },

    setTextBellowButton(value: string): void {
      if (!this.question) {
        return;
      }

      modifyQuiz("set", value, ["textBellowButton"]);
    }
  },

  computed: {
    questionTitle(): string {
      return this.question?.question || "";
    },

    questionDescription(): string {
      return this.question?.contacts?.paragraph || "";
    },

    questionPlaceholder(): string {
      return this.question?.placeholder || "";
    },

    variants(): QuestionVariant[] {
      if (!this.question || !Array.isArray(this.question.variants)) {
        return [];
      }

      return this.question.variants as QuestionVariant[];
    },

    normalizedVariants(): QuestionVariant[] {
      return this.variants.map((variant, variantIndex) => {
        return {
          text: variant.text || "",
          img: variant.img || "",
          _key: variant._key || this.createVariantKey(variantIndex)
        };
      });
    },
    textBellowButton(): string {
      return modifyQuiz("get", "", ["textBellowButton"]) ?? "";
    }
  }
});
</script>
